import {DocumentPart, DocumentTemplateData, PartType} from '@dv5/domain'
import {v4 as uuidv4} from 'uuid'

export const PART_INDEX_STEP = 100

export interface DocumentPartForm {
    id: string
    partHeader: string
    partContent: string
    partType: PartType
    partIndex: number
}

export interface DocumentTemplateFormData {
    sourceDocumentId: string
    title: string
    parts: DocumentPartForm[]
}

export function getNextPartIndex(parts: DocumentPartForm[]): number {
    if (!parts || parts.length === 0) {
        return PART_INDEX_STEP
    }

    const maxEntityIndex = parts.reduce((max, item) => {
        return item.partIndex > max ? item.partIndex : max
    }, -Infinity)

    return Math.max(PART_INDEX_STEP, maxEntityIndex + PART_INDEX_STEP)
}

export function normalizePart(part?: Partial<DocumentPart> | null): DocumentPartForm {
    return {
        id: part?.id ?? uuidv4(),
        partHeader: part?.partHeader ?? '',
        partContent: part?.partContent ?? '',
        partType: part?.partType ?? PartType.PLAINTEXT,
        partIndex: part?.partIndex ?? PART_INDEX_STEP,
    }
}

export function normalizeParts(parts?: Array<Partial<DocumentPart>> | null): DocumentPartForm[] {
    return Array.isArray(parts) ? parts.map(normalizePart) : []
}

export function addPart(parts: DocumentPartForm[]): DocumentPartForm[] {
    const newPart: DocumentPartForm = {
        id: uuidv4(),
        partHeader: '',
        partContent: '',
        partType: PartType.PLAINTEXT,
        partIndex: getNextPartIndex(parts),
    }
    return [...parts, newPart]
}

export function removePart(parts: DocumentPartForm[], index: number): DocumentPartForm[] {
    return parts.filter((_, i) => i !== index)
}

export function updatePart(parts: DocumentPartForm[], index: number, part: DocumentPartForm): DocumentPartForm[] {
    return parts.map((item, i) => (i === index ? part : item))
}

function reindexParts(parts: DocumentPartForm[]): DocumentPartForm[] {
    return parts.map((part, index) => ({...part, partIndex: (index + 1) * PART_INDEX_STEP}))
}

export function movePart(parts: DocumentPartForm[], from: number, to: number): DocumentPartForm[] {
    if (from === to) {
        return parts
    }

    const next = [...parts]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    return reindexParts(next)
}

export function toDocumentTemplateData(formData: DocumentTemplateFormData): DocumentTemplateData {
    return {
        sourceDocumentId: formData.sourceDocumentId,
        title: formData.title,
        parts: formData.parts.map((part) => ({
            id: part.id,
            partContent: part.partContent,
            partType: part.partType,
            partIndex: part.partIndex,
            partHeader: part.partHeader || null,
            tags: null,
            commentsCount: 0,
        })),
    }
}
